import { useEffect, useState } from 'react';
import ErrorBanner from '../../components/ErrorBanner';
import Modal from '../../components/Modal';
import { listLanguages } from '../../api/languages';
import { listCategories } from '../../api/categories';
import { listSubcategories } from '../../api/subcategories';
import { listLyrics } from '../../api/lyrics';
import {
  createSong,
  deleteSong,
  downloadSongsExport,
  importSongs,
  listSongs,
  updateSong,
} from '../../api/songs';

const emptyDetails = {
  title: '',
  title_ta: '',
  title_thanglish: '',
  song_number: '',
  scale: '',
  category_ids: [],
  subcategory_id: '',
};

export default function SongsSection({ token }) {
  const [songs, setSongs] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [allLyrics, setAllLyrics] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [details, setDetails] = useState(emptyDetails);
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const [importSummary, setImportSummary] = useState(null);

  const load = async () => {
    setIsLoading(true);
    setError('');
    try {
      const [songsData, languagesData, categoriesData, subcategoriesData, lyricsData] =
        await Promise.all([
          listSongs(token),
          listLanguages(token),
          listCategories(token),
          listSubcategories(token),
          listLyrics(token),
        ]);
      setSongs(songsData);
      setLanguages(languagesData);
      setCategories(categoriesData);
      setSubcategories(subcategoriesData);
      setAllLyrics(lyricsData);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const languageNameById = (id) => languages.find((l) => l.language_id === id)?.name || `#${id}`;
  const categoryNames = (ids) =>
    ids.map((id) => categories.find((c) => c.category_id === id)?.name || `#${id}`).join(', ');
  const subcategoryName = (id) =>
    id == null ? null : subcategories.find((s) => s.subcategory_id === id)?.name || `#${id}`;
  const songLanguageNames = (song) => {
    const ids = new Set(
      song.lyrics_ids.map((id) => allLyrics.find((l) => l.lyrics_id === id)?.language_id)
    );
    return [...ids].filter(Boolean).map(languageNameById).join(', ');
  };

  const openCreate = () => {
    setEditing(null);
    setDetails(emptyDetails);
    setFormError('');
    setModalOpen(true);
  };

  const openEdit = (song) => {
    setEditing(song);
    setDetails({
      title: song.title,
      title_ta: song.title_ta || '',
      title_thanglish: song.title_thanglish || '',
      song_number: song.song_number ?? '',
      scale: song.scale,
      category_ids: song.category_ids,
      subcategory_id: song.subcategory_id ?? '',
    });
    setFormError('');
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const toggleCategory = (id) => {
    setDetails((prev) => ({
      ...prev,
      category_ids: prev.category_ids.includes(id)
        ? prev.category_ids.filter((c) => c !== id)
        : [...prev.category_ids, id],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (details.category_ids.length === 0) {
      setFormError('Select at least one category.');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        title: details.title,
        title_ta: details.title_ta,
        title_thanglish: details.title_thanglish,
        song_number: details.song_number === '' ? null : Number(details.song_number),
        scale: details.scale,
        category_ids: details.category_ids,
        subcategory_id: details.subcategory_id === '' ? null : Number(details.subcategory_id),
      };

      if (editing) {
        await updateSong(token, editing.song_id, payload);
      } else {
        await createSong(token, payload);
      }
      closeModal();
      await load();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (song) => {
    if (!window.confirm(`Delete song "${song.title}"?`)) return;
    try {
      await deleteSong(token, song.song_id);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    setError('');
    try {
      await downloadSongsExport(token);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setIsImporting(true);
    setImportError('');
    setImportSummary(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const summary = await importSongs(token, formData);
      setImportSummary(summary);
      await load();
    } catch (err) {
      setImportError(err.message);
    } finally {
      setIsImporting(false);
    }
  };

  const canCreate = categories.length > 0;

  return (
    <section>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2 className="text-xl font-semibold text-gray-900">Songs</h2>
        <div className="flex items-center gap-3">
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {isExporting ? 'Exporting...' : 'Export current songs'}
          </button>
          <label
            className={`rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer ${
              isImporting ? 'opacity-50 pointer-events-none' : ''
            }`}
          >
            {isImporting ? 'Importing...' : 'Import from Excel'}
            <input
              type="file"
              accept=".xlsx,.xlsm"
              onChange={handleImportFile}
              disabled={isImporting}
              className="hidden"
            />
          </label>
          <button
            onClick={openCreate}
            disabled={!canCreate}
            title={canCreate ? '' : 'Add a category first'}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            + New song
          </button>
        </div>
      </div>

      <p className="text-sm text-gray-500 mb-4">
        Create a song here first, then add its lyrics (and each lyrics' YouTube link) from the
        Lyrics section — or use "Export current songs" to get an .xlsx sheet with every song
        and its lyrics in all three languages, edit it, and "Import from Excel" to create new
        songs and update existing ones (matched by Song Number) in bulk.
      </p>

      {importError && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
          {importError}
        </div>
      )}

      {importSummary && (
        <div className="mb-4 bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
            <p className="text-sm text-gray-700">
              <span className="font-medium">{importSummary.created}</span> created,{' '}
              <span className="font-medium">{importSummary.updated}</span> updated,{' '}
              <span className={`font-medium ${importSummary.errors > 0 ? 'text-red-600' : ''}`}>
                {importSummary.errors}
              </span>{' '}
              failed
            </p>
            <button
              onClick={() => setImportSummary(null)}
              className="text-sm text-gray-500 hover:underline"
            >
              Dismiss
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <tbody className="divide-y divide-gray-100">
                {importSummary.results.map((r) => (
                  <tr key={r.row}>
                    <td className="px-4 py-1.5 text-gray-500 whitespace-nowrap">Row {r.row}</td>
                    <td className="px-4 py-1.5 text-gray-600 whitespace-nowrap">
                      No. {r.song_number ?? '—'}
                    </td>
                    <td className="px-4 py-1.5 whitespace-nowrap">
                      <span
                        className={
                          r.status === 'error'
                            ? 'text-red-600'
                            : r.status === 'created'
                            ? 'text-indigo-600'
                            : 'text-gray-600'
                        }
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-1.5 text-gray-600">
                      {r.status === 'error' ? r.message : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ErrorBanner message={error} onRetry={load} />

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Title (English)</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Title (Tamil)</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Title (Thanglish)</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">No.</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Scale</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Languages</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Categories</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Subcategory</th>
              <th className="text-right px-4 py-2 font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              <tr>
                <td colSpan={9} className="px-4 py-6 text-center text-gray-400">
                  Loading...
                </td>
              </tr>
            ) : songs.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-6 text-center text-gray-400">
                  No songs yet
                </td>
              </tr>
            ) : (
              songs.map((song) => (
                <tr key={song.song_id}>
                  <td className="px-4 py-2 text-gray-900">{song.title}</td>
                  <td className="px-4 py-2 text-gray-600">{song.title_ta || '—'}</td>
                  <td className="px-4 py-2 text-gray-600">{song.title_thanglish || '—'}</td>
                  <td className="px-4 py-2 text-gray-600">{song.song_number ?? '—'}</td>
                  <td className="px-4 py-2 text-gray-600">{song.scale}</td>
                  <td className="px-4 py-2 text-gray-600">{songLanguageNames(song) || '—'}</td>
                  <td className="px-4 py-2 text-gray-600">{categoryNames(song.category_ids)}</td>
                  <td className="px-4 py-2 text-gray-600">
                    {subcategoryName(song.subcategory_id) || '—'}
                  </td>
                  <td className="px-4 py-2 text-right space-x-3 whitespace-nowrap">
                    <button
                      onClick={() => openEdit(song)}
                      className="text-gray-600 hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(song)}
                      className="text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {modalOpen && (
        <Modal title={editing ? `Edit ${editing.title}` : 'New song'} onClose={closeModal}>
          <form onSubmit={handleSubmit} className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title (English)
              </label>
              <input
                required
                value={details.title}
                onChange={(e) => setDetails({ ...details, title: e.target.value })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title (Tamil)
              </label>
              <input
                required
                value={details.title_ta}
                onChange={(e) => setDetails({ ...details, title_ta: e.target.value })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title (Thanglish)
              </label>
              <input
                required
                value={details.title_thanglish}
                onChange={(e) => setDetails({ ...details, title_thanglish: e.target.value })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Song number (optional)
              </label>
              <input
                type="number"
                min="1"
                value={details.song_number}
                onChange={(e) => setDetails({ ...details, song_number: e.target.value })}
                placeholder="e.g. 534"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Scale</label>
              <input
                required
                value={details.scale}
                onChange={(e) => setDetails({ ...details, scale: e.target.value })}
                placeholder="e.g. G Major"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Categories</label>
              <div className="flex flex-wrap gap-2">
                {categories.map((c) => (
                  <label
                    key={c.category_id}
                    className="inline-flex items-center gap-1.5 text-sm border border-gray-300 rounded-md px-2 py-1"
                  >
                    <input
                      type="checkbox"
                      checked={details.category_ids.includes(c.category_id)}
                      onChange={() => toggleCategory(c.category_id)}
                    />
                    {c.name}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subcategory (optional)
              </label>
              <select
                value={details.subcategory_id}
                onChange={(e) => setDetails({ ...details, subcategory_id: e.target.value })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">None</option>
                {subcategories.map((s) => (
                  <option key={s.subcategory_id} value={s.subcategory_id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            {editing && (
              <p className="text-xs text-gray-500 border-t border-gray-200 pt-3">
                Manage this song's lyrics and YouTube links from the Lyrics section.
              </p>
            )}

            {formError && <p className="text-sm text-red-600">{formError}</p>}

            <button
              type="submit"
              disabled={isSaving}
              className="w-full rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </form>
        </Modal>
      )}
    </section>
  );
}
